import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import Ley40GateQuestionnaire from '@/components/Ley40GateQuestionnaire';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Shield,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  ScanSearch,
  Users,
  Landmark,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { LanguageToggle } from '@/components/layout/LanguageToggle';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { cn } from '@/lib/utils';

const CISA_SECTORS = [
  'Chemical Sector',
  'Commercial Facilities Sector',
  'Communications Sector',
  'Critical Manufacturing Sector',
  'Dams Sector',
  'Defense Industrial Base Sector',
  'Emergency Services Sector',
  'Energy Sector',
  'Financial Services Sector',
  'Food and Agriculture Sector',
  'Government Facilities Sector',
  'Healthcare and Public Health Sector',
  'Information Technology Sector',
  'Nuclear Reactors, Materials, and Waste Sector',
  'Transportation Systems Sector',
  'Water and Wastewater Systems Sector',
];

const REGULATORY_DOMAINS = ['justice.gov', 'pr.gov', 'prits.pr.gov', 'asg.pr.gov', 'oig.pr.gov'];

function isGovEmail(email) {
  if (!email || !email.includes('@')) return false;
  const domain = email.toLowerCase().split('@')[1];
  return REGULATORY_DOMAINS.some(d => domain === d || domain.endsWith('.' + d));
}

export default function Register() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  // Determine initial step
  const initialStep = searchParams.get('step') === 'role' ? 5 : 1;

  const [step, setStep] = useState(initialStep);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Step 1 data
  const [name, setName] = useState('');
  const [entityType, setEntityType] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [entityName, setEntityName] = useState('');
  const [entityUrl, setEntityUrl] = useState('');
  const [entityDescription, setEntityDescription] = useState('');

  // Step 2 data
  const [cisaSectors, setCisaSectors] = useState([]);
  const [prGov, setPrGov] = useState({ q1: false, q2: false, q3: false });
  const [fedGov, setFedGov] = useState({ q1: false, q2: false });

  // Step 4 data
  const [code, setCode] = useState('');

  // Step 5 data
  const [selectedRole, setSelectedRole] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [pendingUser, setPendingUser] = useState(null);

  // If returning for role selection, load pending data
  useEffect(() => {
    if (initialStep === 5) {
      const token = localStorage.getItem('vrs_token');
      const pending = localStorage.getItem('vrs_pending_user');
      if (token && pending) {
        setVerifyToken(token);
        setPendingUser(JSON.parse(pending));
        setEmail(JSON.parse(pending).email || '');
      } else {
        navigate('/register');
      }
    }
  }, [initialStep, navigate]);

  const toggleSector = (sector) => {
    setCisaSectors(prev =>
      prev.includes(sector) ? prev.filter(s => s !== sector) : [...prev, sector]
    );
  };

  // Step 1 validation
  const validateStep1 = () => {
    if (!name.trim()) return 'Full name is required';
    if (!entityType) return 'Entity type is required';
    if (!email.trim()) return 'Email is required';
    if (!password) return 'Password is required';
    if (!entityName.trim()) return 'Entity name is required';
    if (!entityUrl.trim()) return 'Entity URL is required';
    return null;
  };

  const handleNext = () => {
    setError('');
    if (step === 1) {
      const err = validateStep1();
      if (err) { setError(err); return; }
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setError('');
    if (step > 1 && step !== 5) setStep(step - 1);
  };

  // Step 3: Submit registration
  const handleSubmitRegistration = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        name: name.trim(),
        entity_type: entityType,
        email: email.trim().toLowerCase(),
        password,
        entity_name: entityName.trim(),
        entity_url: entityUrl.trim(),
        entity_description: entityDescription.trim(),
        cisa_sectors: cisaSectors,
        pr_government_business: {
          does_business_with_pr_government: prGov.q1,
          provides_public_services_or_contracts: prGov.q2,
          handles_governmental_data_or_ict: prGov.q3,
        },
        federal_government_business: {
          has_far_contracts: fedGov.q1,
          has_dfars_contracts: fedGov.q2,
        },
      };
      const res = await authAPI.register(payload);
      // [Phase 3(c) Stage 1, §8] Registration-flow coherence: when email
      // verification is disabled the backend auto-verifies the account and
      // returns a role-selection token instead of a code. Skip the (dead)
      // code-entry step and go straight to role selection so the user is never
      // stranded on a screen asking for a code that was never sent.
      if (res.data?.auto_verified && res.data?.token) {
        localStorage.setItem('vrs_token', res.data.token);
        setVerifyToken(res.data.token);
        setPendingUser(res.data.user);
        setStep(5);
        toast.success('Account created', { description: 'Email verification is disabled — select your role to finish.' });
      } else {
        setStep(4);
        toast.success('Registration submitted!', { description: 'Check your email for the verification code.' });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Registration failed';
      setError(msg);
      toast.error('Error', { description: msg });
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Verify email
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { setError('Please enter the full 6-digit code'); return; }
    setLoading(true);
    setError('');
    try {
      const response = await authAPI.verifyEmail(email.trim().toLowerCase(), code);
      const { token, user } = response.data;
      setVerifyToken(token);
      setPendingUser(user);
      localStorage.setItem('vrs_token', token);
      setStep(5);
      toast.success('Email verified!', { description: 'Now select your role to complete registration.' });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Invalid verification code';
      setError(msg);
      toast.error('Verification failed', { description: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await authAPI.resendCode(email.trim().toLowerCase());
      toast.success('Code resent', { description: 'Check your email.' });
    } catch {
      toast.error('Failed to resend code');
    }
  };

  // Step 5: Set role
  const handleSetRole = async () => {
    if (!selectedRole) { setError('Please select a role'); return; }
    setLoading(true);
    setError('');
    try {
      const response = await authAPI.setRole(selectedRole);
      const { token, user } = response.data;
      localStorage.removeItem('vrs_pending_user');
      login(token, user);
      // [Phase 3(c) Sub-stage 2, deliverable 1 / D-i] The account + token now
      // exist, so advance to the POST-AUTH Ley 40 applicability gate step, which
      // fetches the DB-served questions from the authenticated endpoint. Skipping
      // it leaves the user provisional (never blocked).
      setStep(6);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to set role';
      setError(msg);
      toast.error('Error', { description: msg });
    } finally {
      setLoading(false);
    }
  };

  const canSelectRegulatory = isGovEmail(email || pendingUser?.email || '');

  const stepTitles = {
    1: 'User Information',
    2: 'Scope Questions',
    3: 'Review & Submit',
    4: 'Email Verification',
    5: 'Select Your Role',
    6: 'Ley 40 Applicability',
  };

  return (
    <div className="min-h-screen bg-background vrs-grid relative flex items-center justify-center p-4 md:p-6">
      {/* Language toggle */}
      <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
        <LanguageToggle />
        <ThemeToggle />
      </div>
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(600px circle at 50% 30%, rgba(26,209,230,0.08), transparent 55%)' }}
      />
      <div className="w-full max-w-[640px] relative">
        {/* Logo + Step indicator */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 border border-primary/20">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight text-foreground">VRS</h1>
              <p className="text-[10px] text-muted-foreground">Vendor Risk Scanner</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {[1,2,3,4,5,6].map(s => (
              <div
                key={s}
                className={cn(
                  'h-2 rounded-full transition-all duration-300',
                  s === step ? 'w-8 bg-primary' : s < step ? 'w-2 bg-primary/60' : 'w-2 bg-border'
                )}
              />
            ))}
          </div>
        </div>

        <Card className="border-border/60 bg-card/80 backdrop-blur-sm shadow-[var(--vrs-shadow-elev-2)]">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-[10px]">
                Step {step} of 6
              </Badge>
            </div>
            <CardTitle className="text-xl font-semibold tracking-tight">
              {stepTitles[step]}
            </CardTitle>
            {step === 1 && <CardDescription>Provide your basic information to create an account</CardDescription>}
            {step === 2 && <CardDescription>Help us understand your compliance scope</CardDescription>}
            {step === 3 && <CardDescription>Review your information and submit to receive a verification code</CardDescription>}
            {step === 4 && <CardDescription>Enter the 6-digit code sent to {email}</CardDescription>}
            {step === 5 && <CardDescription>Choose how you will use VRS</CardDescription>}
            {step === 6 && <CardDescription>Answer the Puerto Rico Ley 40 applicability questions, or skip and complete this later in Settings.</CardDescription>}
          </CardHeader>

          <CardContent>
            {/* STEP 1: User Information */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="reg-name">Full Name *</Label>
                    <Input id="reg-name" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" data-testid="register-name-input" className="h-10 bg-muted/30 border-border" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-entity-type">Entity Type *</Label>
                    <Select value={entityType} onValueChange={setEntityType}>
                      <SelectTrigger data-testid="register-entity-type-select" className="h-10 bg-muted/30 border-border">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Government Agency">Government Agency</SelectItem>
                        <SelectItem value="Company">Company</SelectItem>
                        <SelectItem value="Individual">Individual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="reg-email">Entity Email Address *</Label>
                    <Input id="reg-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" data-testid="register-email-input" className="h-10 bg-muted/30 border-border" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-password">Password *</Label>
                    <div className="relative">
                      <Input id="reg-password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a password" data-testid="register-password-input" className="h-10 bg-muted/30 border-border pr-10" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="reg-entity-name">Entity Name *</Label>
                    <Input id="reg-entity-name" value={entityName} onChange={e => setEntityName(e.target.value)} placeholder="Acme Corp" data-testid="register-entity-name-input" className="h-10 bg-muted/30 border-border" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-entity-url">Entity URL *</Label>
                    <Input id="reg-entity-url" type="url" value={entityUrl} onChange={e => setEntityUrl(e.target.value)} placeholder="https://acme.com" data-testid="register-entity-url-input" className="h-10 bg-muted/30 border-border" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-description">Entity Description (optional)</Label>
                  <Textarea id="reg-description" value={entityDescription} onChange={e => setEntityDescription(e.target.value)} placeholder="Briefly describe your organization..." data-testid="register-description-input" className="bg-muted/30 border-border min-h-[80px]" />
                </div>
                {error && <p data-testid="register-error-text" className="text-sm text-destructive">{error}</p>}
                <div className="flex justify-between pt-2">
                  <Button variant="ghost" className="text-muted-foreground" onClick={() => navigate('/login')}>
                    <ArrowLeft className="mr-2 h-4 w-4" />Back to login
                  </Button>
                  <Button onClick={handleNext} data-testid="register-next-step1">
                    Next <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 2: Scope Questions */}
            {step === 2 && (
              <div className="space-y-6">
                {/* CISA Sectors */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">CISA Critical Infrastructure Sectors</Label>
                  <p className="text-xs text-muted-foreground">Select all sectors that apply to your entity.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[240px] overflow-y-auto pr-2">
                    {CISA_SECTORS.map(sector => (
                      <label
                        key={sector}
                        className={cn(
                          'flex items-center gap-2.5 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-[background-color,border-color] duration-200',
                          cisaSectors.includes(sector)
                            ? 'bg-accent/40 border-primary/40 text-foreground'
                            : 'border-border/60 text-muted-foreground hover:border-border hover:bg-muted/20'
                        )}
                      >
                        <Checkbox
                          checked={cisaSectors.includes(sector)}
                          onCheckedChange={() => toggleSector(sector)}
                          data-testid={`cisa-sector-${sector.replace(/\s+/g, '-').toLowerCase()}`}
                        />
                        <span className="text-xs leading-tight">{sector}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <Separator className="opacity-50" />

                {/* PR Government Business */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Puerto Rico Government Business</Label>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between rounded-lg border border-border/60 px-4 py-3">
                      <span className="text-xs text-foreground/90 pr-4 leading-relaxed">Do you do business with the Government of Puerto Rico?</span>
                      <Switch checked={prGov.q1} onCheckedChange={v => setPrGov({...prGov, q1: v})} data-testid="pr-gov-q1" />
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border/60 px-4 py-3">
                      <span className="text-xs text-foreground/90 pr-4 leading-relaxed">Do you provide services or perform contracts that involve public functions, public services, or use/investment of public funds, or work under governmental authority?</span>
                      <Switch checked={prGov.q2} onCheckedChange={v => setPrGov({...prGov, q2: v})} data-testid="pr-gov-q2" />
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border/60 px-4 py-3">
                      <span className="text-xs text-foreground/90 pr-4 leading-relaxed">Do you manage, store, transmit, or otherwise handle governmental data or assets of information, or supply ICT services to agencies?</span>
                      <Switch checked={prGov.q3} onCheckedChange={v => setPrGov({...prGov, q3: v})} data-testid="pr-gov-q3" />
                    </div>
                    {(prGov.q1 || prGov.q2 || prGov.q3) && (
                      <div className="rounded-lg border border-vrs-amber/30 bg-vrs-amber/5 px-4 py-3">
                        <p className="text-xs text-vrs-amber">Answering 'yes' to any of these will include you in a compliance review for vendors to government entities.</p>
                      </div>
                    )}
                  </div>
                </div>

                <Separator className="opacity-50" />

                {/* Federal Government Business */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Federal Government Business</Label>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between rounded-lg border border-border/60 px-4 py-3">
                      <span className="text-xs text-foreground/90 pr-4">Do you have any contracts with FAR Provisions?</span>
                      <Switch checked={fedGov.q1} onCheckedChange={v => setFedGov({...fedGov, q1: v})} data-testid="fed-gov-q1" />
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border/60 px-4 py-3">
                      <span className="text-xs text-foreground/90 pr-4">Do you have any contracts with DFARS Provisions?</span>
                      <Switch checked={fedGov.q2} onCheckedChange={v => setFedGov({...fedGov, q2: v})} data-testid="fed-gov-q2" />
                    </div>
                  </div>
                </div>

                {error && <p data-testid="register-error-text" className="text-sm text-destructive">{error}</p>}
                <div className="flex justify-between pt-2">
                  <Button variant="ghost" className="text-muted-foreground" onClick={handleBack}>
                    <ArrowLeft className="mr-2 h-4 w-4" />Back
                  </Button>
                  <Button onClick={handleNext} data-testid="register-next-step2">
                    Next <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3: Review & Submit */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="rounded-lg border border-border/60 bg-muted/10 p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground text-xs">Name</span><p className="font-medium text-foreground">{name}</p></div>
                    <div><span className="text-muted-foreground text-xs">Entity Type</span><p className="font-medium text-foreground">{entityType}</p></div>
                    <div><span className="text-muted-foreground text-xs">Email</span><p className="font-medium text-foreground">{email}</p></div>
                    <div><span className="text-muted-foreground text-xs">Entity Name</span><p className="font-medium text-foreground">{entityName}</p></div>
                    <div className="col-span-2"><span className="text-muted-foreground text-xs">Entity URL</span><p className="font-medium text-foreground">{entityUrl}</p></div>
                  </div>
                  {cisaSectors.length > 0 && (
                    <div>
                      <span className="text-muted-foreground text-xs">CISA Sectors</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {cisaSectors.map(s => <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>)}
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Clicking Submit will send a 6-digit verification code to <strong>{email}</strong>.</p>
                {error && <p data-testid="register-error-text" className="text-sm text-destructive">{error}</p>}
                <div className="flex justify-between pt-2">
                  <Button variant="ghost" className="text-muted-foreground" onClick={handleBack}>
                    <ArrowLeft className="mr-2 h-4 w-4" />Back
                  </Button>
                  <Button onClick={handleSubmitRegistration} data-testid="register-submit-button" disabled={loading}>
                    {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</> : <><Check className="mr-2 h-4 w-4" />Submit</>}
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 4: Email Verification */}
            {step === 4 && (
              <form onSubmit={handleVerifyEmail} className="space-y-4">
                <div className="space-y-2">
                  <Label>Verification code</Label>
                  <div className="flex justify-center" data-testid="register-otp-input">
                    <InputOTP maxLength={6} value={code} onChange={v => { setCode(v); setError(''); }}>
                      <InputOTPGroup>
                        {[0,1,2,3,4,5].map(i => (
                          <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg border-border bg-muted/30" />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
                {error && <p data-testid="register-error-text" className="text-sm text-destructive">{error}</p>}
                <Button type="submit" data-testid="register-verify-button" className="w-full h-11 font-semibold" disabled={loading || code.length !== 6}>
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying...</> : 'Verify email'}
                </Button>
                <Button type="button" variant="ghost" className="w-full text-muted-foreground text-xs" onClick={handleResendCode} data-testid="register-resend-code">
                  Didn't receive the code? Resend
                </Button>
              </form>
            )}

            {/* STEP 5: Role Selection */}
            {step === 5 && (
              <div className="space-y-4">
                <div className="space-y-3">
                  {/* Compliance */}
                  <button
                    type="button"
                    onClick={() => setSelectedRole('compliance')}
                    data-testid="register-role-compliance"
                    className={cn(
                      'w-full flex items-start gap-4 rounded-lg border px-4 py-4 text-left transition-[background-color,border-color] duration-200 cursor-pointer',
                      selectedRole === 'compliance'
                        ? 'bg-accent/40 border-primary/50'
                        : 'border-border/60 hover:bg-muted/20 hover:border-border'
                    )}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-vrs-green/10 border border-vrs-green/30 mt-0.5">
                      <ScanSearch className="h-5 w-5 text-vrs-green" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">Compliance</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Check your own compliance score</p>
                      <p className="text-[10px] text-muted-foreground/70 mt-1">Scan your own entity's external-facing assets</p>
                    </div>
                    {selectedRole === 'compliance' && <Check className="h-5 w-5 text-primary ml-auto shrink-0 mt-1" />}
                  </button>

                  {/* Procurement */}
                  <button
                    type="button"
                    onClick={() => setSelectedRole('procurement')}
                    data-testid="register-role-procurement"
                    className={cn(
                      'w-full flex items-start gap-4 rounded-lg border px-4 py-4 text-left transition-[background-color,border-color] duration-200 cursor-pointer',
                      selectedRole === 'procurement'
                        ? 'bg-accent/40 border-primary/50'
                        : 'border-border/60 hover:bg-muted/20 hover:border-border'
                    )}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-vrs-cyan/10 border border-vrs-cyan/30 mt-0.5">
                      <Users className="h-5 w-5 text-vrs-cyan" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">Procurement</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Check scores of other entities</p>
                      <p className="text-[10px] text-muted-foreground/70 mt-1">Scan third-party vendors and partners</p>
                    </div>
                    {selectedRole === 'procurement' && <Check className="h-5 w-5 text-primary ml-auto shrink-0 mt-1" />}
                  </button>

                  {/* Regulatory */}
                  <button
                    type="button"
                    onClick={() => canSelectRegulatory && setSelectedRole('regulatory')}
                    data-testid="register-role-regulatory"
                    disabled={!canSelectRegulatory}
                    className={cn(
                      'w-full flex items-start gap-4 rounded-lg border px-4 py-4 text-left transition-[background-color,border-color] duration-200',
                      !canSelectRegulatory ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                      selectedRole === 'regulatory'
                        ? 'bg-accent/40 border-primary/50'
                        : canSelectRegulatory
                          ? 'border-border/60 hover:bg-muted/20 hover:border-border'
                          : 'border-border/40 bg-muted/10'
                    )}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-vrs-amber/10 border border-vrs-amber/30 mt-0.5">
                      <Landmark className="h-5 w-5 text-vrs-amber" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">Regulatory</p>
                      <p className="text-xs text-muted-foreground mt-0.5">View database of all scans and results</p>
                      <p className="text-[10px] text-muted-foreground/70 mt-1">For authorized government regulators only</p>
                      {!canSelectRegulatory && (
                        <div className="flex items-center gap-1.5 mt-2">
                          <Lock className="h-3 w-3 text-muted-foreground" />
                          <p className="text-[10px] text-muted-foreground">Regulatory accounts require a .gov email domain.</p>
                        </div>
                      )}
                    </div>
                    {selectedRole === 'regulatory' && <Check className="h-5 w-5 text-primary ml-auto shrink-0 mt-1" />}
                  </button>
                </div>

                {error && <p data-testid="register-error-text" className="text-sm text-destructive">{error}</p>}

                <Button
                  onClick={handleSetRole}
                  data-testid="register-complete-button"
                  className="w-full h-11 font-semibold"
                  disabled={loading || !selectedRole}
                >
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Completing...</> : 'Complete Registration'}
                </Button>
              </div>
            )}

            {/* [Phase 3(c) Sub-stage 2, deliverable 1 / D-i] Post-auth Ley 40
                applicability gate. Same component as the /settings section. Skip
                or Finish both proceed to the dashboard; skipping leaves the user
                provisional (never blocked). */}
            {step === 6 && (
              <div className="space-y-4" data-testid="register-step6">
                <Ley40GateQuestionnaire />
                <div className="flex items-center justify-between gap-3">
                  <button type="button" onClick={() => navigate('/dashboard')} className="text-xs text-muted-foreground hover:text-foreground" data-testid="register-gate-skip">Skip for now</button>
                  <Button onClick={() => navigate('/dashboard')} className="h-11 font-semibold" data-testid="register-gate-finish">Finish</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer link */}
        {step <= 3 && (
          <p className="text-center text-xs text-muted-foreground mt-4">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-medium hover:text-primary/80">Sign in</Link>
          </p>
        )}
      </div>
    </div>
  );
}
